// ============================================
// TTS API 모듈 (ES6)
// Cloud Run TTS 프록시를 통한 Google Cloud TTS 호출
// ============================================

import { getVoicePreset, VOICE_PRESETS } from '../../config.js';
import { authFetch } from '../../shared/auth-fetch.js';
import { TTS_ENDPOINTS } from '../../shared/api-endpoints.js';

// ============================================
// Cloud Run TTS API 호출 (Google Cloud TTS)
// ============================================

/**
 * 텍스트를 음성으로 변환 (Google Cloud TTS - Chirp 3: HD)
 * @param {string} text - 변환할 텍스트
 * @param {string} voiceId - 음성 프리셋 ID (30개 Chirp 3: HD 음성)
 * @param {string} emotion - 감정 ID (optional, 현재 미사용)
 * @returns {Promise<string>} - 오디오 데이터 URL
 */
export async function synthesizeSpeech(text, voiceId, emotion = null) {
  if (!text || !voiceId) {
    throw new Error('텍스트와 음성 ID가 필요합니다');
  }

  const response = await authFetch(TTS_ENDPOINTS.synthesize, {
    body: { text, voiceId }
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.error || 'TTS 변환 실패');
  }

  const data = await response.json();

  if (!data.success || !data.audioContent) {
    throw new Error('TTS 응답이 올바르지 않습니다');
  }

  // Google Cloud TTS는 MP3 형식으로 반환
  const mimeType = data.mimeType || 'audio/mp3';
  return `data:${mimeType};base64,` + data.audioContent;
}

// ============================================
// 미리듣기 기능
// ============================================

/**
 * 미리듣기용 TTS 생성
 * @param {string} text - 샘플 텍스트
 * @param {string} voiceId - 음성 프리셋 ID
 * @returns {Promise<HTMLAudioElement>} - 오디오 엘리먼트
 */
export async function previewTTS(text, voiceId) {
  const audioUrl = await synthesizeSpeech(text, voiceId);
  const audio = new Audio(audioUrl);
  return audio;
}

// ============================================
// 텍스트 전처리
// ============================================

/**
 * TTS용 텍스트 정리
 * - 괄호 안 지문/설명 제거
 * - 별표 액션 제거
 * - 마커 태그 제거
 * - 마크다운 제거
 */
export function cleanTextForTTS(text) {
  if (!text) return '';

  return text
    // 괄호 안 지문/설명 제거 (최우선): (손을 들며), （생각하며）
    .replace(/[（(][^)）]*[)）]/g, '')
    // 【타이틀】 제거: 【금전운】, 【총운】 등
    .replace(/【[^】]*】/g, '')
    // 별표 액션 제거: *미소 짓는다*
    .replace(/\*[^*]+\*/g, '')
    // 마커 태그 제거: {C}, {C0}, {N}, {T}, {C:happy}, {N:dramatic} 등
    .replace(/\{[CNT]\d?(?::[a-zA-Z]+)?\}/g, '')
    // 마크다운 링크 제거 [text](url) -> text
    .replace(/\[([^\]]+)\]\([^)]+\)/g, '$1')
    // 마크다운 굵게/기울임 제거 (별표 액션 제거 후 남은 것)
    .replace(/\*\*([^*]+)\*\*/g, '$1')
    .replace(/__([^_]+)__/g, '$1')
    .replace(/_([^_]+)_/g, '$1')
    // 남은 별표 마크다운 제거 (짝이 안 맞는 ** 등)
    .replace(/\*+/g, '')
    // 코드 블록 제거
    .replace(/```[\s\S]*?```/g, '')
    .replace(/`([^`]+)`/g, '$1')
    // 연속 공백 정리
    .replace(/\s+/g, ' ')
    .trim();
}

// ============================================
// 문장 분리
// ============================================

/**
 * 텍스트를 문장 단위로 분리
 * @param {string} text - 분리할 텍스트
 * @returns {string[]} - 문장 배열
 */
export function splitIntoSentences(text) {
  if (!text) return [];

  // 문장 종결 패턴: . ! ? 뒤에 공백이나 끝
  const sentences = [];
  const regex = /[^.!?]*[.!?]+(?:\s|$)|[^.!?]+$/g;
  let match;

  while ((match = regex.exec(text)) !== null) {
    const sentence = match[0].trim();
    if (sentence) {
      sentences.push(sentence);
    }
  }

  return sentences;
}

/**
 * 대사만 추출 (나레이션, 생각 제외) - 레거시 호환
 * @param {string} text - 전체 텍스트
 * @returns {string} - 대사만 추출된 텍스트
 */
export function extractDialogueOnly(text) {
  if (!text) return '';

  const result = extractDialogueAndNarration(text);
  return result.dialogues.map(d => d.text).join(' ');
}

/**
 * 대사와 나레이션을 분리하여 추출
 * 형식: {C}, {C:happy}, {N}, {N:dramatic}, {T} (생각은 TTS 제외)
 * 참고: 현재 TTS는 대사({C})만 처리, 나레이션({N})은 건너뜀
 * @param {string} text - 전체 텍스트
 * @returns {{ dialogues: Array<{text: string, emotion: string|null, index: number}>, narrations: Array<{text: string, emotion: string|null, index: number}> }}
 */
export function extractDialogueAndNarration(text) {
  if (!text) return { dialogues: [], narrations: [] };

  const result = { dialogues: [], narrations: [] };

  // 감정 태그 포함 마커 파싱: {C}, {C:happy}, {C0}, {N:dramatic} 등
  // 형식: {마커타입(:감정)?숫자?}내용
  const markerRegex = /\{([CNT])(?::([a-zA-Z]+))?(\d?)\}([^{]*)/g;
  let match;

  while ((match = markerRegex.exec(text)) !== null) {
    const markerType = match[1];  // C, N, T
    const emotion = match[2] || null;  // happy, dramatic, etc. (optional)
    const content = cleanBrackets(match[4].trim());
    const index = match.index;

    if (!content) continue;

    if (markerType === 'C') {
      // 캐릭터 대사
      result.dialogues.push({ text: content, emotion, index });
    } else if (markerType === 'N') {
      // 나레이션
      result.narrations.push({ text: content, emotion, index });
    }
    // T(생각)는 TTS에서 제외
  }

  // 마커가 없는 경우: 구 형식 호환 (전체를 대사로 처리)
  if (result.dialogues.length === 0 && result.narrations.length === 0) {
    const cleaned = cleanBrackets(text.trim());
    if (cleaned) {
      result.dialogues.push({ text: cleaned, emotion: null, index: 0 });
    }
  }

  return result;
}

/**
 * 괄호 안 지문 제거 헬퍼
 */
function cleanBrackets(text) {
  if (!text) return '';
  return text
    // 괄호 안 지문 제거: (손을 들며), （생각하며）
    .replace(/[（(][^)）]*[)）]/g, '')
    // 별표 액션 제거: *미소 짓는다*
    .replace(/\*[^*]+\*/g, '')
    // 연속 공백 정리
    .replace(/\s+/g, ' ')
    .trim();
}

// ============================================
// 음성 프리셋 목록 조회
// ============================================

export function getVoicePresetList() {
  return Object.values(VOICE_PRESETS);
}

export function getVoicesByGender(gender) {
  return Object.values(VOICE_PRESETS).filter(v => v.gender === gender);
}
